import { routes } from '@/constants/routes'

import { AUTH_OPEN_ROUTES, AUTH_ROUTES } from './constants'

/** Query param carrying the path the user tried to open before login. */
export const RETURN_URL_PARAM = 'returnUrl'

/** sessionStorage key for that path. Survives the login flow in the same tab. */
export const RETURN_URL_STORAGE_KEY = 'auth.returnUrl'

/**
 * Request header set by the proxy so a later 401 (session expired mid-render)
 * can forward the page the user was opening.
 */
export const RETURN_PATH_HEADER = 'x-return-path'

const RETURN_URL_MAX_LENGTH = 2048
const RETURN_URL_BASE = 'https://return.invalid'

const BLOCKED_ROUTES = [...AUTH_ROUTES, ...AUTH_OPEN_ROUTES] as const

/**
 * Accept only same-origin app paths. Rejects absolute URLs, protocol-relative
 * URLs, auth pages, and anything the URL parser resolves off-origin.
 */
export function getSafeReturnPath(candidate: string | null | undefined): string | null {
  if (!candidate) return null

  const value = candidate.trim()
  if (value.length === 0 || value.length > RETURN_URL_MAX_LENGTH) return null
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\'))
    return null
  if (value.includes('\\') || value.includes('\0') || /[\r\n]/.test(value)) return null

  let decoded: string
  try {
    decoded = decodeURIComponent(value)
  } catch {
    return null
  }
  // `%2f%2f` decodes to a protocol-relative URL. Keep the redirect on this host.
  if (!decoded.startsWith('/') || decoded.startsWith('//') || decoded.includes('\\')) {
    return null
  }

  let url: URL
  try {
    url = new URL(value, RETURN_URL_BASE)
  } catch {
    return null
  }

  if (url.origin !== RETURN_URL_BASE) return null
  if (url.username || url.password) return null
  if (!url.pathname.startsWith('/') || url.pathname.startsWith('//')) return null

  const blocked = BLOCKED_ROUTES.some(
    (route) => url.pathname === route || url.pathname.startsWith(`${route}/`),
  )
  if (blocked || url.pathname.startsWith('/api')) return null

  return `${url.pathname}${url.search}${url.hash}`
}

export function toReturnPathHeader(pathname: string, search: string): string | null {
  const value = `${pathname}${search}`
  if (value.length > RETURN_URL_MAX_LENGTH || /[\r\n]/.test(value)) return null
  return value
}

export function rememberReturnUrl(candidate: string | null | undefined): void {
  if (globalThis.window === undefined) return

  const safe = getSafeReturnPath(candidate)
  if (!safe) return

  try {
    sessionStorage.setItem(RETURN_URL_STORAGE_KEY, safe)
  } catch (error) {
    console.error('Failed to save return URL:', error)
  }
}

/** Persist the page the user is on, before sending them to login. */
export function rememberCurrentReturnUrl(): void {
  if (globalThis.location === undefined) return
  rememberReturnUrl(`${globalThis.location.pathname}${globalThis.location.search}`)
}

/**
 * Path to open after a successful login. Falls back to home when nothing
 * safe was stored. Does not delete the value — the authenticated shell does,
 * so a repeated effect cannot clobber the destination.
 */
export function readReturnUrl(): string {
  if (globalThis.window === undefined) return routes.index

  try {
    return (
      getSafeReturnPath(sessionStorage.getItem(RETURN_URL_STORAGE_KEY)) ?? routes.index
    )
  } catch (error) {
    console.error('Failed to read return URL:', error)
    return routes.index
  }
}

export function clearReturnUrl(): void {
  if (globalThis.window === undefined) return

  try {
    sessionStorage.removeItem(RETURN_URL_STORAGE_KEY)
  } catch (error) {
    console.error('Failed to clear return URL:', error)
  }
}
